// The web app's one player, SoundCloud style: it lives at the app root, so
// what is playing keeps playing while you move between pages, and the bar
// docked at the bottom is the same everywhere.
//
// Whatever list you press play from becomes the queue — the feed page, a
// profile's seeds, your own — and next / previous walk it. The <audio>
// lives in the bar and exists only while a track is loaded, so closing the
// bar is stopping. Audio URLs are presigned by the core API (or, on pages
// still reading the account worker, its /r2/object stream); both support
// Range, so a preview starts before it has all arrived and a seek asks only
// for the bytes it lands on.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router";
import { Loader2, Pause, Play, SkipBack, SkipForward, X } from "lucide-react";
import { Button } from "@web-shared/components/ui/button";
import { mediaTime } from "@web-shared/lib/format";
import { json, webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { track as trackAnalytics } from "@web-shared/lib/analytics";
import { useBottomChrome } from "./chrome";

export interface PlayerTrack {
  /** Seed id — what "this card is playing" compares. */
  id: string;
  title: string;
  /** A URL the browser can stream (presigned, or a same-origin stream). */
  url: string;
  producer: { handle: string; username: string } | null;
  /** Where the title links: the seed's page. */
  href: string;
  /** The seed whose play count this counts toward — a master, not a stem. */
  countsFor?: string;
  /** One track of a seed: its own name, carried rather than parsed back out
   * of `title` (which is "<seed> — <track>" so the player bar reads whole). */
  trackName?: string;
}

/** Seconds of listening before a play counts, as the big players do. */
export const PLAY_COUNTS_AFTER_S = 5;

export type PlayerStatus = "loading" | "playing" | "paused";

/** Where to jump: a fraction of the track, or a moment in it. */
type Seek = { fraction: number } | { seconds: number };

function seekSeconds(to: Seek, duration: number): number {
  return "fraction" in to ? to.fraction * duration : Math.min(to.seconds, duration);
}

interface PlayerApi {
  track: PlayerTrack | null;
  status: PlayerStatus;
  /** The seed whose last play failed, until it is tried again. */
  failedId: string | null;
  /**
   * A card's play button: start `track` (with `queue` as what next/previous
   * walk), or pause / resume it if it is the one loaded.
   */
  toggle: (track: PlayerTrack, queue?: PlayerTrack[]) => void;
  /**
   * A click on a waveform: jump to `fraction` of `track` — starting it there
   * if it is not the one loaded.
   */
  seekTo: (track: PlayerTrack, fraction: number, queue?: PlayerTrack[]) => void;
  /** The same, to a moment in seconds — a timed comment's "at 1:23". */
  seekToSeconds: (track: PlayerTrack, seconds: number, queue?: PlayerTrack[]) => void;
  /** Forget the moment last chosen (see `useSeekMark`) — a posted comment has
   * spent it, and the next one should pin wherever playback then is. */
  clearMark: () => void;
  playPause: () => void;
  close: () => void;
}

const PlayerContext = createContext<PlayerApi | null>(null);

/** Where the loaded track is, and the moment last chosen in it. Its own
 * context: it changes several times a second, and only what draws progress
 * should re-render with it. */
interface PlayerProgress {
  id: string | null;
  time: number;
  duration: number;
  /** The moment a deliberate jump last landed on — a click on the waveform, a
   * drag of the bar, a comment's "at 1:23" — as opposed to wherever playback
   * has since run to. Null once it is spent or a track starts from the top. */
  mark: { id: string; seconds: number } | null;
}
const ProgressContext = createContext<PlayerProgress>({ id: null, time: 0, duration: 0, mark: null });

/** How far through `id` playback is (0–1), or null when `id` is not loaded. */
export function useTrackProgress(id: string): number | null {
  const p = useContext(ProgressContext);
  if (p.id !== id) return null;
  return Number.isFinite(p.duration) && p.duration > 0 ? Math.min(1, p.time / p.duration) : 0;
}

/** Seconds into the loaded track, when it is `id`. */
export function useTrackTime(id: string): number | null {
  const p = useContext(ProgressContext);
  return p.id === id ? p.time : null;
}

/**
 * The moment a click chose in `id`, in seconds — what a comment pins to when
 * one was chosen. Clicking the waveform at 1:20 and writing about it means
 * 1:20, not the 1:35 playback has reached by the time the sentence is typed.
 */
export function useSeekMark(id: string): number | null {
  const p = useContext(ProgressContext);
  return p.mark && p.mark.id === id ? p.mark.seconds : null;
}

export function usePlayer(): PlayerApi {
  const api = useContext(PlayerContext);
  if (!api) throw new Error("usePlayer outside <PlayerProvider>.");
  return api;
}

/** What a card shows for its own track: its status when loaded, else null. */
export function useTrackStatus(id: string): PlayerStatus | null {
  const { track, status } = usePlayer();
  return track?.id === id ? status : null;
}

/** The bar's own height on a phone, and the gap it floats on. */
const MINIPLAYER_H = "calc(var(--miniplayer-h) + 0.5rem)";

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [queue, setQueue] = useState<PlayerTrack[]>([]);
  const [index, setIndex] = useState(-1);
  const [status, setStatus] = useState<PlayerStatus>("loading");
  const [failedId, setFailedId] = useState<string | null>(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // A seek asked for before the audio knew its length, applied once it does.
  const pendingSeek = useRef<Seek | null>(null);
  // The moment last chosen by a click, for whatever pins to it.
  const [mark, setMark] = useState<{ id: string; seconds: number } | null>(null);
  // Which load of which track has been counted: once per load, not per replay.
  const counted = useRef<string | null>(null);
  const loadSeq = useRef(0);
  const track = index >= 0 ? (queue[index] ?? null) : null;

  const load = useCallback((nextQueue: PlayerTrack[], nextIndex: number, startAt: Seek | null = null) => {
    pendingSeek.current = startAt;
    // Pressing play on a track chooses nothing; a jump into one is resolved
    // against its length below, once the element knows it.
    if (startAt === null) setMark(null);
    loadSeq.current += 1;
    setQueue(nextQueue);
    setIndex(nextIndex);
    setStatus("loading");
    setTime(0);
    setDuration(0);
  }, []);

  const playPause = useCallback(() => {
    const el = audio.current;
    if (!el) return;
    // play() on an ended element starts it over, which is what the button means then.
    if (el.paused) void el.play().catch(() => setStatus("paused"));
    else el.pause();
  }, []);

  const toggle = useCallback(
    (next: PlayerTrack, nextQueue?: PlayerTrack[]) => {
      setFailedId(null);
      if (track?.id === next.id && track.url === next.url) {
        playPause();
        return;
      }
      const list = nextQueue && nextQueue.some((t) => t.id === next.id) ? nextQueue : [next];
      load(list, list.findIndex((t) => t.id === next.id));
    },
    [track, playPause, load],
  );

  const seek = useCallback(
    (next: PlayerTrack, to: Seek, nextQueue?: PlayerTrack[]) => {
      const el = audio.current;
      if (track?.id === next.id && track.url === next.url && el) {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          const seconds = seekSeconds(to, el.duration);
          el.currentTime = seconds;
          setMark({ id: next.id, seconds });
        } else pendingSeek.current = to;
        if (el.paused) void el.play().catch(() => setStatus("paused"));
        return;
      }
      setFailedId(null);
      const list = nextQueue && nextQueue.some((t) => t.id === next.id) ? nextQueue : [next];
      load(list, list.findIndex((t) => t.id === next.id), to);
    },
    [track, load],
  );
  const seekTo = useCallback(
    (next: PlayerTrack, fraction: number, nextQueue?: PlayerTrack[]) =>
      seek(next, { fraction: Math.min(Math.max(fraction, 0), 1) }, nextQueue),
    [seek],
  );
  const seekToSeconds = useCallback(
    (next: PlayerTrack, seconds: number, nextQueue?: PlayerTrack[]) => seek(next, { seconds: Math.max(0, seconds) }, nextQueue),
    [seek],
  );

  const step = useCallback(
    (by: 1 | -1) => {
      const el = audio.current;
      // Previous past the first few seconds restarts the track, as players do.
      if (by === -1 && el && el.currentTime > 3) {
        el.currentTime = 0;
        return;
      }
      const to = index + by;
      if (to >= 0 && to < queue.length) load(queue, to);
    },
    [index, queue, load],
  );

  const clearMark = useCallback(() => setMark(null), []);

  const close = useCallback(() => {
    setQueue([]);
    setIndex(-1);
    setMark(null);
  }, []);

  // Lock screen, headphones, the OS media keys.
  useEffect(() => {
    const session = typeof navigator !== "undefined" ? navigator.mediaSession : undefined;
    if (!session) return;
    if (!track) {
      session.metadata = null;
      return;
    }
    session.metadata = new MediaMetadata({ title: track.title, artist: track.producer?.username ?? "", album: "Seedbed" });
    session.setActionHandler("play", playPause);
    session.setActionHandler("pause", playPause);
    session.setActionHandler("nexttrack", index < queue.length - 1 ? () => step(1) : null);
    session.setActionHandler("previoustrack", () => step(-1));
  }, [track, index, queue.length, playPause, step]);

  // The room editor has a transport of its own, wired to its own audio graph.
  // Two players on one phone screen is two play buttons and two sounds, so the
  // bar stands down while you are in a room — but the <audio> stays mounted
  // below, so the queue and the position are still there when you come back.
  const inRoom = useLocation().pathname.startsWith("/room/");
  useEffect(() => {
    if (inRoom) audio.current?.pause();
  }, [inRoom]);

  const api = useMemo<PlayerApi>(
    () => ({ track, status, failedId, toggle, seekTo, seekToSeconds, clearMark, playPause, close }),
    [track, status, failedId, toggle, seekTo, seekToSeconds, clearMark, playPause, close],
  );
  const progress = useMemo<PlayerProgress>(
    () => ({ id: track?.id ?? null, time, duration, mark }),
    [track, time, duration, mark],
  );

  return (
    <PlayerContext.Provider value={api}>
      <ProgressContext.Provider value={progress}>
      {children}
      {track && (
        <PlayerBar
          hidden={inRoom}
          track={track}
          status={status}
          time={time}
          duration={duration}
          hasNext={index < queue.length - 1}
          onPlayPause={playPause}
          onStep={step}
          onSeek={(sec) => {
            const el = audio.current;
            if (!el) return;
            el.currentTime = sec;
            setTime(sec);
            setMark({ id: track.id, seconds: sec });
          }}
          onClose={close}
        >
          <audio
            ref={audio}
            key={`${track.id}:${track.url}`}
            src={track.url}
            autoPlay
            onPlaying={() => setStatus("playing")}
            onWaiting={() => setStatus("loading")}
            onPause={() => setStatus("paused")}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime;
              setTime(t);
              // A listen counts once it has lasted; the server dedupes per hour.
              const load = `${loadSeq.current}:${track.id}`;
              if (track.countsFor && t >= PLAY_COUNTS_AFTER_S && counted.current !== load) {
                counted.current = load;
                void webApi(`/seeds/${encodeURIComponent(track.countsFor)}/play`, json("POST")).catch(() => {});
                trackAnalytics("seed_played", { seedId: track.countsFor });
              }
            }}
            onDurationChange={(e) => {
              const el = e.currentTarget;
              setDuration(el.duration);
              const to = pendingSeek.current;
              if (to !== null && Number.isFinite(el.duration) && el.duration > 0) {
                pendingSeek.current = null;
                const seconds = seekSeconds(to, el.duration);
                el.currentTime = seconds;
                setMark({ id: track.id, seconds });
              }
            }}
            onEnded={() => {
              // Roll on through the queue; the last track stays loaded, paused.
              if (index < queue.length - 1) load(queue, index + 1);
            }}
            onError={() => {
              setFailedId(track.id);
              // One bad object in a queue is not the end of the queue: roll on,
              // and only close when there is nothing left to try.
              if (index < queue.length - 1) load(queue, index + 1);
              else close();
            }}
          />
        </PlayerBar>
      )}
      </ProgressContext.Provider>
    </PlayerContext.Provider>
  );
}

function PlayerBar({
  hidden,
  track,
  status,
  time,
  duration,
  hasNext,
  onPlayPause,
  onStep,
  onSeek,
  onClose,
  children,
}: {
  /** Mounted but stood down — the <audio> below still holds the queue. */
  hidden: boolean;
  track: PlayerTrack;
  status: PlayerStatus;
  time: number;
  duration: number;
  hasNext: boolean;
  onPlayPause: () => void;
  onStep: (by: 1 | -1) => void;
  onSeek: (seconds: number) => void;
  onClose: () => void;
  children: ReactNode;
}) {
  // While the thumb is held the bar shows where it WILL go; the element only
  // seeks on release, rather than issuing a range request per pixel dragged.
  const [draft, setDraft] = useState<number | null>(null);
  const dragging = useRef(false);
  const seekable = Number.isFinite(duration) && duration > 0;
  const shown = draft ?? time;
  const pct = seekable ? Math.min(100, (shown / duration) * 100) : 0;

  const commit = () => {
    dragging.current = false;
    if (draft !== null) onSeek(draft);
    setDraft(null);
  };

  // Whatever bottom chrome the current page put up (the phone tab bar, on the
  // pages that have one) — the bar docks on top of it rather than guessing its
  // height, which is how the two used to drift apart.
  const chrome = useBottomChrome();

  if (hidden) return <>{children}</>;

  return (
    <>
      {/* Keeps the end of every page clear of the bar. On a phone that is the
          bar's own height plus whatever it is sitting on, so a page with no tab
          bar gets the smaller number rather than the old flat 80px that fit
          neither case. An inline height cannot carry a breakpoint, hence two. */}
      <div aria-hidden className="sm:hidden" style={{ height: `calc(${chrome} + ${MINIPLAYER_H})` }} />
      <div aria-hidden className="hidden h-20 sm:block" />

      {/* ---- Mobile: rounded mini player above tab bar ---- */}
      <div
        role="region"
        aria-label="Now playing"
        className="fixed inset-x-2 z-40 h-14 rounded-2xl bg-raised/95 backdrop-blur border border-white/10 flex items-center gap-3 px-2.5 overflow-hidden sm:hidden"
        style={{ bottom: `calc(${chrome} + 0.5rem)` }}
      >
        <Link to={track.href} className="relative size-[38px] shrink-0 rounded-full bg-black overflow-hidden">
          <div
            className={cn(
              "absolute inset-0 rounded-full",
              status === "playing" && "animate-[spin_5s_linear_infinite]",
            )}
            style={{
              background: "repeating-radial-gradient(#111 0px, #151515 2px, #0a0a0a 3px, #111 4px)",
            }}
          >
            <div className="absolute inset-0 m-auto size-[36%] rounded-full bg-muted-foreground/40" />
          </div>
        </Link>
        <Link to={track.href} className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{track.title}</div>
          {track.producer && (
            <div className="text-[11px] text-muted-foreground/60 truncate">{track.producer.username}</div>
          )}
        </Link>
        <button
          type="button"
          onClick={onPlayPause}
          aria-label={status === "playing" ? "Pause" : "Play"}
          className="size-10 rounded-full bg-white text-black flex items-center justify-center shrink-0"
        >
          {status === "loading" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : status === "playing" ? (
            <Pause className="size-4" />
          ) : (
            <Play className="size-4 ml-0.5" />
          )}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close player"
          className="size-7 rounded-full flex items-center justify-center text-muted-foreground/50 shrink-0"
        >
          <X className="size-3.5" />
        </button>
        {/* Progress line at bottom */}
        <div className="absolute left-0 bottom-0 h-[2px] bg-primary" style={{ width: `${pct}%` }} />
      </div>

      {/* ---- Desktop: full player bar ---- */}
      <div
        role="region"
        aria-label="Now playing"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)] hidden sm:block"
      >
        <div className="mx-auto flex max-w-[1300px] items-center gap-3 px-6 py-2">
          <div className="flex shrink-0 items-center">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onStep(-1)}
              title="Previous"
              aria-label="Previous"
            >
              <SkipBack />
            </Button>
            <Button
              size="icon"
              onClick={onPlayPause}
              className="rounded-full"
              title={status === "playing" ? "Pause" : "Play"}
              aria-label={status === "playing" ? `Pause ${track.title}` : `Play ${track.title}`}
            >
              {status === "loading" ? <Loader2 className="animate-spin" /> : status === "playing" ? <Pause /> : <Play />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className={cn(!hasNext && "opacity-40")}
              disabled={!hasNext}
              onClick={() => onStep(1)}
              title="Next"
              aria-label="Next"
            >
              <SkipForward />
            </Button>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <p className="min-w-0 flex-1 truncate text-sm">
                <Link to={track.href} className="font-medium hover:underline">
                  {track.title}
                </Link>
                {track.producer && (
                  <>
                    <span className="text-muted-foreground"> · </span>
                    <Link to={`/@${track.producer.handle}`} className="text-muted-foreground hover:text-foreground">
                      {track.producer.username}
                    </Link>
                  </>
                )}
              </p>
              <span className="tabular shrink-0 text-[11px] text-muted-foreground">
                {mediaTime(shown)} / {seekable ? mediaTime(duration) : "–:––"}
              </span>
            </div>
            <input
              type="range"
              aria-label={`Seek in ${track.title}`}
              min={0}
              max={seekable ? duration : 1}
              step={0.1}
              value={seekable ? Math.min(shown, duration) : 0}
              disabled={!seekable}
              className="block h-4 w-full cursor-pointer accent-[var(--primary)] disabled:cursor-default"
              onPointerDown={() => {
                dragging.current = true;
              }}
              onPointerUp={commit}
              onPointerCancel={commit}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (dragging.current) setDraft(v);
                else onSeek(v);
              }}
            />
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} className="shrink-0" title="Close" aria-label="Close player">
            <X />
          </Button>
        </div>
      </div>
      {/* Audio element — always in the DOM, outside both bar variants */}
      {children}
    </>
  );
}
